import axios from 'axios';

import {
	buildPresencePut,
	type IPresenceAnswer,
	type IPresenceError,
	type ISetAgentPresenceRequest,
	readPresenceError,
	toPresenceRead,
} from '../components/pages/upload-result/agent-presence';
import { COREAPI, getUserData } from '../constants/global';

/**
 * Standalone client for api.core's `AgentCoverage` controller ("polling agents" on screen).
 *
 * Auxiliary to the result, so — like `agent-assignment-api.ts` — it has NO 401→logout
 * interceptor and resolves every status (`validateStatus`) so the caller can classify the
 * body. Unlike the Redux thunks it carries a timeout: the app default is none at all, and a
 * dropped socket would otherwise leave "Saving polling agents…" spinning forever.
 */
const presenceApi = axios.create({ baseURL: COREAPI, timeout: 20_000 });

presenceApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// api.core sometimes answers JSON with a Content-Type axios will not auto-parse.
presenceApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is (401/403 bodies are empty by design).
		}
	}
	return response;
});

/**
 * Build-time switch, `REACT_APP_AGENT_PRESENCE_ENABLED=true` — set in `.env.production` (the
 * staging image) and NOT in `.env.cluster` (the `-prod` image). Keep it off for a host until the
 * api.core image that admits Party Agents to these routes is ROLLED — before
 * that every call is an empty-body 403. Check the deployed tag, not a status probe:
 *   kubectl --context do-lon1-k8s-ielect -n ielect get deployment ielect-core \
 *     -o jsonpath='{.spec.template.spec.containers[0].image}'
 * Support can switch it per device without a build: localStorage `ielect-agent-presence` =
 * `on` | `off`.
 */
export const AGENT_PRESENCE_ENABLED = process.env.REACT_APP_AGENT_PRESENCE_ENABLED === 'true';
export const AGENT_PRESENCE_OVERRIDE_KEY = 'ielect-agent-presence';

export const agentPresenceEnabled = (): boolean => {
	try {
		const override = localStorage.getItem(AGENT_PRESENCE_OVERRIDE_KEY);
		if (override === 'on') return true;
		if (override === 'off') return false;
	} catch {
		// Storage blocked — fall through to the build default.
	}
	return AGENT_PRESENCE_ENABLED;
};

export interface IRawResponse {
	/** 0 when no response arrived (offline, timeout, CORS). */
	status: number;
	body: unknown;
}

const send = async (run: () => Promise<{ status: number; data: unknown }>): Promise<IRawResponse> => {
	try {
		const res = await run();
		return { status: res.status, body: res.data };
	} catch {
		return { status: 0, body: undefined };
	}
};

export const getPresence = (electionId: number, pollingUnitCode: string): Promise<IRawResponse> =>
	send(() =>
		presenceApi.get('/api/v1/AgentCoverage/by-polling-unit', {
			params: { electionId, pollingUnitCode },
			validateStatus: () => true,
		})
	);

export const putPresence = (body: ISetAgentPresenceRequest): Promise<IRawResponse> =>
	send(() => presenceApi.put('/api/v1/AgentCoverage', body, { validateStatus: () => true }));

export type PresenceSaveOutcome =
	| { status: 'saved'; present: number; absent: number }
	/** Nothing to write: no answers and nothing stored, or answers identical to the store. */
	| { status: 'unchanged' }
	| { status: 'failed'; error: IPresenceError };

/** What the submission screen shows: the outcome, plus the two states around it. */
export type PresenceSaveState = PresenceSaveOutcome | { status: 'idle' } | { status: 'saving' };

/**
 * Read → merge → full replace. Called after the result itself has been accepted, and never
 * able to affect it.
 *
 * The read is repeated here rather than trusted from when the screen opened, because the PUT
 * replaces the whole unit: a verifier's answer added in between would otherwise be erased by
 * a body built from a stale read. And if that read FAILS, nothing is written — saving over a
 * record we could not see is the one unrecoverable mistake available here.
 */
export const savePresence = async (
	{ electionId, pollingUnitCode, answers }: { electionId: number; pollingUnitCode: string; answers: readonly IPresenceAnswer[] },
	client: { get: typeof getPresence; put: typeof putPresence } = { get: getPresence, put: putPresence }
): Promise<PresenceSaveOutcome> => {
	const current = await client.get(electionId, pollingUnitCode);
	const read = toPresenceRead(current.status, current.body);
	if (read.kind === 'error') return { status: 'failed', error: read.error };

	const plan = buildPresencePut({
		electionId,
		pollingUnitCode,
		answers,
		stored: read.kind === 'stored' ? read.parties : [],
	});
	if (!plan.hasChanges) return { status: 'unchanged' };

	const res = await client.put(plan.body);
	const written = toPresenceRead(res.status, res.body);
	if (written.kind !== 'stored') {
		return { status: 'failed', error: written.kind === 'error' ? written.error : readPresenceError(res.status, res.body) };
	}
	return {
		status: 'saved',
		present: plan.body.parties.filter((p) => p.isPresent).length,
		absent: plan.body.parties.filter((p) => !p.isPresent).length,
	};
};
